import { useEffect, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  ClipboardList,
  Users,
  BarChart,
  Target,
  Star,
  FileText,
  CheckCircle,
  ArrowRight,
} from 'lucide-react'
import { LandingHeader } from '../components/landing/LandingHeader'
import { LandingFooter } from '../components/landing/LandingFooter'

gsap.registerPlugin(ScrollTrigger)

export const Route = createFileRoute('/kinerja-product')({
  component: KinerjaProductPage,
})

const features = [
  {
    Icon: ClipboardList,
    title: 'Psychometric Assessments',
    description: 'Run Big Five, DISC, Leadership, and fully custom tests. Question types include scale, single/multi-choice, and open text — with automatic competency scoring.',
  },
  {
    Icon: FileText,
    title: 'Assessment Templates & Sessions',
    description: 'Build reusable assessment templates with sections and competency mappings. Assign sessions to employees with due dates and track completion status.',
  },
  {
    Icon: Users,
    title: 'Competency Dictionary',
    description: "Define your organization's competency framework with proficiency levels. Map competencies to positions and track gaps across every employee.",
  },
  {
    Icon: Target,
    title: 'KPI Metrics Tracking',
    description: 'Set, track, and report on key performance indicators at the individual and team level with configurable metric definitions.',
  },
  {
    Icon: Star,
    title: 'Org Level Management',
    description: 'Define org levels (L1–Lx) with clear seniority bands. Assign employees to levels and use them in competency requirements and assessments.',
  },
  {
    Icon: BarChart,
    title: 'Performance Reports',
    description: 'Generate individual competency gap reports and export organizational performance insights to drive data-backed HR decisions.',
  },
]

const benefits = [
  'Run psychometric tests (Big Five, DISC, Leadership) with zero setup time',
  'Track competency growth across every employee in real time',
  'Replace manual review spreadsheets with structured assessment sessions',
  "Build a custom competency framework that reflects your organization's values",
]

// ─── Hero Mockup ───────────────────────────────────────────────────────────────

function KinerjaHeroMockup() {
  const competencies = [
    { label: 'Leadership', pct: 78, color: '#359d9e' },
    { label: 'Delivery', pct: 91, color: '#27777a' },
    { label: 'Collaboration', pct: 65, color: '#359d9e' },
    { label: 'Initiative', pct: 82, color: '#27777a' },
  ]
  return (
    <div className="relative w-full h-full select-none">
      {/* Main panel */}
      <div className="absolute rounded-2xl overflow-hidden shadow-[0px_8px_40px_0px_rgba(53,157,158,0.18)] bg-white border border-[rgba(53,157,158,0.12)]"
        style={{ top: '8%', left: '0%', right: '8%', bottom: '6%' }}>
        <div className="flex items-center gap-1.5 px-3 py-2 bg-[#f8fffe] border-b border-[rgba(53,157,158,0.1)]">
          <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <div className="flex-1 mx-3 h-4 rounded bg-[#e6e7e7] text-[9px] text-[#b3b7b7] flex items-center px-2">pelajari.ai/kinerja</div>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[#f2fbfa] border-2 border-[#359d9e]/20 flex items-center justify-center text-[10px] font-bold text-[#359d9e] flex-shrink-0">RP</div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-[#1a2727]">Reza Pratama</p>
              <p className="text-[9px] text-[#b3b7b7]">Product Manager · L3</p>
            </div>
            <div className="bg-[#f2fbfa] border border-[#d5f2ef] text-[#27777a] text-[8px] font-semibold px-2 py-0.5 rounded-full">Q1 Review</div>
          </div>
          <div className="bg-[#f8fffe] rounded-lg p-2.5 flex flex-col gap-2">
            <p className="text-[8px] font-bold text-[#b3b7b7] uppercase tracking-wider">Competency Scores</p>
            {competencies.map((c) => (
              <div key={c.label} className="flex items-center gap-2">
                <span className="text-[8px] text-[#1a2727] w-[60px] flex-shrink-0">{c.label}</span>
                <div className="flex-1 h-1.5 bg-[#e6e7e7] rounded-full overflow-hidden">
                  <div className="h-1.5 rounded-full" style={{ width: `${c.pct}%`, backgroundColor: c.color }} />
                </div>
                <span className="text-[8px] font-semibold w-5 text-right" style={{ color: c.color }}>{c.pct}</span>
              </div>
            ))}
          </div>
          <div className="border border-[rgba(53,157,158,0.15)] rounded-lg p-2.5 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-semibold text-[#1a2727]">Q4 of 10</p>
              <span className="text-[8px] text-[#fc8213] font-semibold">In Progress</span>
            </div>
            <p className="text-[9px] text-[#1a2727] leading-snug">I take charge in ambiguous situations and guide others toward a solution.</p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((v) => (
                <div key={v} className="flex-1 py-1.5 rounded text-[9px] font-bold text-center border"
                  style={v === 4 ? { backgroundColor: '#359d9e', borderColor: '#359d9e', color: 'white' } : { backgroundColor: 'white', borderColor: '#e6e7e7', color: '#b3b7b7' }}>
                  {v}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Floating card */}
      <div className="kinerja-float-card absolute bg-white rounded-xl border border-[rgba(53,157,158,0.15)] shadow-[0px_4px_20px_0px_rgba(53,157,158,0.2)] px-3 py-2.5 flex items-center gap-2.5"
        style={{ bottom: '0%', right: '0%', maxWidth: '52%' }}>
        <div className="w-6 h-6 rounded-full bg-[#f2fbfa] flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 12 12" className="w-3.5 h-3.5" fill="none">
            <circle cx="6" cy="6" r="5" stroke="#359d9e" strokeWidth="1.2" />
            <path d="M3.5 6L5 7.5L8.5 4" stroke="#359d9e" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <p className="text-[9px] font-bold text-[#1a2727]">Score Updated</p>
          <p className="text-[8px] text-[#b3b7b7]">Leadership +4 pts</p>
        </div>
      </div>
    </div>
  )
}

function KinerjaProductPage() {
  const pageRef = useRef<HTMLDivElement>(null)

  // ── Hero refs
  const heroBadgeRef = useRef<HTMLSpanElement>(null)
  const heroHeadingRef = useRef<HTMLHeadingElement>(null)
  const heroSubRef = useRef<HTMLParagraphElement>(null)
  const heroCtaRef = useRef<HTMLDivElement>(null)
  const heroMockupRef = useRef<HTMLDivElement>(null)

  // ── Features refs
  const featHeadingRef = useRef<HTMLDivElement>(null)
  const featCardRefs = useRef<(HTMLDivElement | null)[]>([])

  // ── Benefits refs
  const benefitsLeftRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Hero entrance timeline
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
      gsap.set([heroBadgeRef.current, heroHeadingRef.current, heroSubRef.current, heroCtaRef.current], { opacity: 0, y: 28 })
      gsap.set(heroMockupRef.current, { opacity: 0, x: 48 })

      tl.to(heroBadgeRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'back.out(1.7)' })
        .to(heroHeadingRef.current, { opacity: 1, y: 0, duration: 0.7 }, '-=0.3')
        .to(heroSubRef.current, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4')
        .to(heroCtaRef.current, { opacity: 1, y: 0, duration: 0.55 }, '-=0.35')
        .to(heroMockupRef.current, { opacity: 1, x: 0, duration: 0.85, ease: 'power2.out' }, 0.15)

      // Mockup idle float
      tl.add(() => {
        gsap.to(heroMockupRef.current, { y: -10, duration: 3.2, ease: 'sine.inOut', yoyo: true, repeat: -1 })
        gsap.to('.kinerja-float-card', { y: -7, duration: 2.9, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 0.7 })
      })

      // ── Features section
      gsap.set(featHeadingRef.current, { opacity: 0, y: 24 })
      ScrollTrigger.create({
        trigger: featHeadingRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(featHeadingRef.current, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }),
      })

      gsap.set(featCardRefs.current.filter(Boolean), { opacity: 0, y: 28 })
      ScrollTrigger.create({
        trigger: featCardRefs.current[0],
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(featCardRefs.current.filter(Boolean), {
          opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.08,
        }),
      })

      // ── Benefits + CTA section
      gsap.set(benefitsLeftRef.current, { opacity: 0, y: 28 })
      ScrollTrigger.create({
        trigger: benefitsLeftRef.current,
        start: 'top 82%',
        once: true,
        onEnter: () => gsap.to(benefitsLeftRef.current, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }),
      })

      const statItems = statsRef.current?.querySelectorAll('.stat-item')
      if (statItems?.length) {
        gsap.set(statItems, { opacity: 0, scale: 0.75 })
        ScrollTrigger.create({
          trigger: statsRef.current,
          start: 'top 85%',
          once: true,
          onEnter: () => gsap.to(statItems, {
            opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.7)', stagger: 0.1,
          }),
        })
      }
    }, pageRef)

    return () => ctx.revert()
  }, [])

  return (
    <div ref={pageRef} className="min-h-screen bg-white overflow-x-hidden font-sans">
      <LandingHeader />

      {/* Hero — 2-col split */}
      <section className="relative bg-gradient-to-br from-[#f2fbfa] to-white py-20 px-6 md:px-10 lg:px-[120px] overflow-hidden">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-center lg:gap-16">
            <div className="flex-1 max-w-[560px]">
              <span ref={heroBadgeRef} className="inline-block bg-[#f2fbfa] border border-[#359d9e] text-[#359d9e] text-sm font-medium px-4 py-2 rounded-lg mb-6">
                Kinerja — Performance Management
              </span>
              <h1 ref={heroHeadingRef} className="text-[36px] font-semibold leading-[44px] tracking-[-1px] text-[#000f0f] sm:text-[48px] sm:leading-[58px] lg:text-[56px] lg:leading-[68px] lg:tracking-[-1.5px] mb-6">
                Master Performance with{' '}
                <span className="text-[#359d9e]">AI</span>
              </h1>
              <p ref={heroSubRef} className="text-base leading-relaxed text-[#1a2727] mb-8">
                Run psychometric assessments, track competency growth, and manage performance review sessions all in one platform. Kinerja gives every employee and HR team a clear, structured view of performance and development.
              </p>
              <div ref={heroCtaRef} className="flex flex-col sm:flex-row gap-4">
                <a href="/login" className="inline-flex items-center justify-center gap-2 bg-[#359d9e] text-white hover:bg-[#27777a] rounded px-6 py-3 font-medium transition-colors">
                  Start Performance Reviews
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#features" className="inline-flex items-center justify-center gap-2 border border-[#359d9e] text-[#359d9e] hover:bg-[#f2fbfa] rounded px-6 py-3 font-medium transition-colors">
                  Explore Features
                </a>
              </div>
            </div>
            <div ref={heroMockupRef} className="w-full max-w-[440px] h-[340px] sm:h-[380px] lg:h-[400px] flex-shrink-0">
              <KinerjaHeroMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-6 md:px-10 lg:px-[120px] max-w-screen-xl mx-auto">
        <div ref={featHeadingRef} className="text-center mb-14">
          <h2 className="text-[32px] font-semibold leading-[40px] tracking-[-0.5px] text-[#000f0f] sm:text-[40px] sm:leading-[48px] mb-4">
            Everything you need for performance excellence
          </h2>
          <p className="text-base text-[#1a2727] max-w-[600px] mx-auto">
            From psychometric design to automated reporting — Kinerja covers the full performance management lifecycle.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => {
            const { Icon } = feature
            return (
              <div
                key={feature.title}
                ref={(el) => { featCardRefs.current[i] = el }}
                className="bg-white border border-[#d5f2ef] rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-[#f2fbfa] rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#359d9e]" />
                </div>
                <h3 className="text-lg font-semibold text-[#000f0f] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#1a2727] leading-relaxed">{feature.description}</p>
              </div>
            )
          })}
        </div>
      </section>

      {/* Benefits + CTA */}
      <section className="bg-[#359d9e] py-16 px-6 md:px-10 lg:px-[120px]">
        <div className="max-w-screen-xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          <div ref={benefitsLeftRef} className="flex-1 text-center lg:text-left">
            <h2 className="text-[30px] font-semibold leading-[38px] text-white mb-6 lg:text-[36px] lg:leading-[45px]">
              Why teams choose Kinerja
            </h2>
            <div className="flex flex-col gap-4 mb-8">
              {benefits.map((b) => (
                <div key={b} className="flex items-start gap-3 justify-center lg:justify-start">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-base text-white">{b}</span>
                </div>
              ))}
            </div>
            <a
              href="/login"
              className="inline-flex items-center gap-2 bg-white text-[#359d9e] hover:bg-white/90 rounded px-6 py-3 font-medium transition-colors"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div ref={statsRef} className="flex-1 bg-white/10 rounded-xl p-8 max-w-[460px] w-full">
            <div className="grid grid-cols-2 gap-6 text-center">
              {[
                { value: '98%', label: 'Time Saved vs Manual' },
                { value: '360°', label: 'Multi-Source Feedback' },
                { value: '1 min', label: 'Gap Analysis Speed' },
                { value: '∞', label: 'Assessment Templates' },
              ].map((stat) => (
                <div key={stat.label} className="stat-item flex flex-col gap-1">
                  <span className="text-[32px] font-semibold text-white">{stat.value}</span>
                  <span className="text-sm text-white/80">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  )
}
